import { useState } from 'react';

import {
  COLOR_TOKENS,
  type ColorToken,
  HorizontalMenuTabs,
  PageHeader,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import {
  type CoachingResponse,
  type CoachRow,
  type CoachTypeSummary,
} from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

import { Box, type SxProps, type Theme, Typography } from '@mui/material';

import { legendDotSx, mutedTextSx } from './analytics.styles';
import { AnalyticsCard } from './AnalyticsCard';
import { useAgentContext } from './CoachingCards.agentContext';

const { coaching } = appStrings.modules.coachingAnalytics;

type CoachType = CoachRow['type'];

const COACH_TYPE_COLORS: Readonly<Record<CoachType, ColorToken>> = {
  backrs: 'Blue/500',
  institutional: 'Green/500',
};

const COACH_TABS: readonly { type: CoachType; label: string; coachLabel: string }[] = [
  {
    type: 'institutional',
    label: coaching.load.institutional,
    coachLabel: coaching.load.institutionalCoach,
  },
  { type: 'backrs', label: coaching.load.backrs, coachLabel: coaching.load.backrsCoach },
];

const coachName = (coach: CoachRow): string =>
  `${coach.first_name ?? ''} ${coach.last_name ?? ''}`.trim();

const CoachingLoadRowItem = ({ row, max }: Readonly<{ row: CoachRow; max: number }>) => {
  const pct = max > 0 ? Math.min((row.caseload / max) * 100, 100) : 0;

  return (
    <Box sx={loadRowSx}>
      <Box sx={loadNameSx}>
        <Box
          sx={legendDotSx}
          style={{ backgroundColor: COLOR_TOKENS[COACH_TYPE_COLORS[row.type]] }}
        />
        <Typography variant="text-sm-medium" sx={loadNameTextSx}>
          {coachName(row)}
        </Typography>
      </Box>
      <Box sx={loadTrackSx}>
        <Box
          sx={loadFillSx}
          style={{ width: `${pct}%`, backgroundColor: COLOR_TOKENS[COACH_TYPE_COLORS[row.type]] }}
        />
      </Box>
      <Typography variant="text-sm-semibold" sx={loadValueSx}>
        {row.caseload}
      </Typography>
    </Box>
  );
};

const HeadlineMetric = ({ label, value }: Readonly<{ label: string; value: number }>) => (
  <Box sx={headlineSx}>
    <Typography variant="display-sm-semibold" sx={headlineValueSx}>
      {value.toLocaleString()}
    </Typography>
    <Typography variant="text-sm-regular" sx={mutedTextSx}>
      {label}
    </Typography>
  </Box>
);

const CoachingLoadCard = ({ data }: Readonly<{ data: CoachingResponse }>) => {
  const [tabIndex, setTabIndex] = useState(0);

  const tab = COACH_TABS[tabIndex];
  const summary: CoachTypeSummary = data.by_type[tab.type];
  const coaches = [...summary.coaches].sort((a, b) => b.caseload - a.caseload);
  const maxCaseload = Math.max(...coaches.map((coach) => coach.caseload), 0);

  return (
    <AnalyticsCard
      title={coaching.load.title}
      subtitle={coaching.load.subtitle}
      info={[coaching.load.info]}
      belowHeader={
        <Box sx={tabsRowSx}>
          <HorizontalMenuTabs
            id="coaching-analytics-coach-type-tabs"
            variant="filter"
            tabs={COACH_TABS.map((option) => option.label)}
            value={tabIndex}
            setValue={setTabIndex}
          />
        </Box>
      }
    >
      <Box sx={headlineRowSx}>
        <HeadlineMetric
          label={coaching.load.avgStudentsPerCoach}
          value={summary.average_per_coach}
        />
        <HeadlineMetric
          label={coaching.load.studentsWithCoach(tab.coachLabel)}
          value={summary.students_with_coach}
        />
      </Box>
      {coaches.length === 0 ? (
        <Typography variant="text-sm-regular" sx={mutedTextSx}>
          {coaching.load.empty}
        </Typography>
      ) : (
        <Box sx={loadListSx}>
          {coaches.map((row) => (
            <CoachingLoadRowItem key={row.id} row={row} max={maxCaseload} />
          ))}
        </Box>
      )}
    </AnalyticsCard>
  );
};

export const CoachingSection = ({ data }: Readonly<{ data: CoachingResponse }>) => {
  useAgentContext(data);

  return (
    <Box sx={sectionSx}>
      <PageHeader
        title={coaching.title}
        subtitle={coaching.subtitle}
        maxWidth={null}
        rightComponents={[
          <LubavButton
            key="coaching-discuss-lubav"
            id="coaching-analytics-coaching-discuss-lubav"
            label={coaching.discussWithLubav}
            promptButtonId="coaching_analytics_coaching"
          />,
        ]}
      />
      <CoachingLoadCard data={data} />
    </Box>
  );
};

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  width: '100%',
});

const tabsRowSx: SxProps<Theme> = {
  display: 'flex',
  width: '100%',
};

const headlineRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-4xl'],
  width: '100%',
});

const headlineSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const headlineValueSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const loadListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const loadRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const loadNameSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
  minWidth: 148,
  flexShrink: 0,
});

const loadNameTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  whiteSpace: 'nowrap',
});

const loadTrackSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flex: 1,
  height: 12,
  borderRadius: backrs.radius['radius-full'],
  backgroundColor: backrs.colors['Colors/Background/bg-quaternary'],
  overflow: 'hidden',
});

const loadFillSx: SxProps<Theme> = ({ backrs }) => ({
  height: '100%',
  borderRadius: backrs.radius['radius-full'],
});

const loadValueSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
  minWidth: 24,
  textAlign: 'right',
  flexShrink: 0,
});
